import { IconButton } from '@mui/material';
import { Button } from '@shared/components/';
import { Text } from '@shared/components/text';
import { Tooltip } from '@shared/components/tooltip';
import { DAT_MCI_URL } from '@shared/constants';
import {
  CachedRequestType,
  IntegrationType,
  MarketConditionsPayload,
} from '@shared/generated/graphql';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { StopType } from 'clerk_common/enums';
import clsx from 'clsx';
import { mean } from 'lodash';
import {
  PiArrowClockwiseFill,
  PiArrowRightBold,
  PiInfo,
  PiTrendDownBold,
  PiTrendUpBold,
} from 'react-icons/pi';
import { RateError } from '../rating-widgets/components/RateError/RateError';
import {
  DATLoadTruckRatio,
  DATLoadTruckRatioChart,
} from './DATLoadTruckRatioChart';
import { DATEquipmentType, DATMCICapacityBar } from './DATMCICapacityBar';
import { GSMarketIndexData } from './types';

const ROOT = makeRootClassName('MarketConditions');
const el = makeElementClassNameFactory(ROOT);

export type DATLoadTruckRatioResponseItem = {
  geoCode: string;
  loadTruckRatios: DATLoadTruckRatio[];
};

type DATMarketConditions = {
  date: string;
  loadSearchCounts: number;
  truckSearchCounts: number;
  mciScore: number;
};

export type DATMarketConditionsIndexResponseItem = {
  date: string;
  geoCode: string;
  summarizedLoadSearchCounts: number;
  summarizedTruckSearchCounts: number;
  summarizedMciScore: number;
  marketConditionsIndexes: DATMarketConditions[];
};

export type DATMarketConditionsForecastResponseItem = {
  geoCode: string;
  marketConditionsForecasts: DATMarketConditions[];
};

const extractLocation = (stop: any) => {
  const { city, state, zip } = stop.location;

  if (city && state) return `${city}, ${state}`;

  return `Postal Code: ${zip}`;
};

interface TriumphMarketIndicatorProps {
  data: GSMarketIndexData;
  label: string;
  title: string;
}

export const TriumphMarketIndicator = (p: TriumphMarketIndicatorProps) => {
  const getIcon = () => {
    switch (p.data.trend) {
      case 'UP':
        return <PiTrendUpBold size={12} />;
      case 'DOWN':
        return <PiTrendDownBold size={12} />;
      default:
        return <PiArrowRightBold size={12} />;
    }
  };

  return (
    <div className={el`load-flow`}>
      <Text type="body-xs">{p.label}</Text>
      <div className={clsx(el`icon`, p.data.trendOutcome)}>
        <Tooltip
          isInstant
          content={`${p.title}\nis ${p.data.instant} with ${p.data.derivative} derivative.`}
        >
          <div className={el`trend-line`}>{getIcon()}</div>
        </Tooltip>
      </div>
    </div>
  );
};

interface TriumphMarketConditionProps {
  marketCondition: MarketConditionsPayload;
}

export const TriumphMarketCondition = (p: TriumphMarketConditionProps) => {
  if (
    p.marketCondition.errorMessage &&
    p.marketCondition.integrationType === IntegrationType.GREENSCREENS
  )
    return (
      <RateError
        errorMessage={p.marketCondition.errorMessage}
        integrationName="Triumph"
        fetchedEntity="market conditions"
      />
    );

  const location = extractLocation(p.marketCondition.stop);
  const stopType = p.marketCondition.stop.stopType as StopType;
  const { inboundLoadFlow, outboundLoadFlow } =
    p.marketCondition.marketConditions[0].apiResponse;

  return (
    <div className={el`market-condition`}>
      <Text type="body-xs" isHeavy className={el`location-name`}>
        {location}
      </Text>
      <div className={el`load-flows`}>
        {stopType === 'PICKUP' && (
          <TriumphMarketIndicator
            data={outboundLoadFlow}
            label="OLF"
            title={`Outbound load flow in ${location}`}
          />
        )}
        {stopType === 'DELIVERY' && (
          <TriumphMarketIndicator
            data={inboundLoadFlow}
            label="ILF"
            title={`Inbound load flow in ${location}`}
          />
        )}
      </div>
    </div>
  );
};

interface DATMarketConditionProps {
  marketCondition: MarketConditionsPayload;
}

// TODO(parlato): Would be great to type this and all children
// components in more detail; currently moving very fast and I
// doubt the types will change given the objects come from DAT
export const DATMarketCondition = (p: DATMarketConditionProps) => {
  if (
    p.marketCondition.errorMessage &&
    p.marketCondition.integrationType === IntegrationType.DAT
  )
    return (
      <RateError
        errorMessage={p.marketCondition.errorMessage}
        integrationName="DAT"
        fetchedEntity="market conditions"
      />
    );

  const marketCondition = p.marketCondition.marketConditions[0];
  const equipmentType = p.marketCondition.equipmentType as DATEquipmentType;
  const response = marketCondition?.apiResponse?.[0];

  if (!response) return null;

  switch (marketCondition.requestType) {
    case CachedRequestType.MARKET_CONDITIONS_INDICES: {
      const { geoCode, summarizedMciScore } =
        response as DATMarketConditionsIndexResponseItem;

      return (
        <div className={el`dat-market-condition`}>
          <div className={el`market-condition`}>
            <Text type="body-xs" isHeavy className={el`location-name`}>
              {geoCode}
            </Text>
          </div>
          <div className="flex flex-row items-center gap-1">
            <Text type="body-xs" className="text-grey-700" isHeavy>
              MCI Gauge
            </Text>
            <IconButton
              title="DAT's MCI site"
              size="small"
              onClick={() => window.open(DAT_MCI_URL, '_blank')}
            >
              <PiInfo />
            </IconButton>
          </div>
          <DATMCICapacityBar
            mciScore={summarizedMciScore}
            equipmentType={equipmentType}
          />
        </div>
      );
    }
    case CachedRequestType.MARKET_CONDITIONS_FORECASTS: {
      const { geoCode, marketConditionsForecasts } =
        response as DATMarketConditionsForecastResponseItem;
      const latestForecast = marketConditionsForecasts[0];
      const { mciScore } = latestForecast;

      return (
        <div className={el`dat-market-condition`}>
          <div className={el`market-condition`}>
            <Text type="body-xs" isHeavy className={el`location-name`}>
              {geoCode}
            </Text>
          </div>
          <div className="flex flex-row items-center gap-1">
            <Text type="body-xs" className="text-grey-700" isHeavy>
              MCI Gauge
            </Text>
            <IconButton
              title="DAT's MCI site"
              size="small"
              onClick={() => window.open(DAT_MCI_URL, '_blank')}
            >
              <PiInfo />
            </IconButton>
          </div>
          <DATMCICapacityBar
            mciScore={mciScore}
            equipmentType={equipmentType}
          />
        </div>
      );
    }

    case CachedRequestType.LOAD_TO_TRUCK_RATIOS: {
      const { geoCode, loadTruckRatios } =
        response as DATLoadTruckRatioResponseItem;
      const ratios = loadTruckRatios.map((ltr: { ratio: string }) =>
        parseFloat(ltr.ratio)
      );
      const averageLTR = Math.round(mean(ratios) * 100) / 100;

      return (
        <div className={el`dat-market-condition`}>
          <div className={el`market-condition`}>
            <Text type="body-xs" isHeavy className={el`location-name`}>
              {geoCode}
            </Text>
            <Text type="body-xs" className={el`average-ltr`}>
              Load-to-truck ratio: <b>{averageLTR}</b>
            </Text>
          </div>
          <Text type="body-xs">8 day load-to-truck trend</Text>
          <div className={el`dat-ltr-chart`}>
            <DATLoadTruckRatioChart data={loadTruckRatios} />
          </div>
        </div>
      );
    }

    default:
      return null;
  }
};

interface SonarMarketConditionProps {
  marketCondition: MarketConditionsPayload;
}

type SonarCoverageGuideLane = {
  origin_market?: string;
  destination_market?: string;
  current_capacity_condition?: string;
  directional_capacity_change?: string;
  coverage_difficulty_score?: number;
  coverage_guidance?: string;
  outbound_tender_rejection_percent?: number;
};

type SonarLaneScore = {
  lane_score?: number;
  capacity_indicator?: string;
  origin_market?: string;
  destination_market?: string;
};

const SonarLaneScoreCard = ({ response }: { response: SonarLaneScore }) => {
  const {
    lane_score: laneScore,
    capacity_indicator: capacityIndicator,
    origin_market: originMarket,
    destination_market: destinationMarket,
  } = response;

  return (
    <>
      <Text type="body-sm" isHeavy>
        Line Score
      </Text>
      <div>
        <div className={el`market-condition`}>
          <Text type="body-xs" className={el`location-name`}>
            Lane:
          </Text>
          <Text type="body-xs" className={el`sonar-ltr-chart`}>
            {originMarket} → {destinationMarket}
          </Text>
        </div>
        <div className={el`market-condition`}>
          <Text type="body-xs" className={el`location-name`}>
            Score:
          </Text>
          <Text type="body-xs" className={el`sonar-ltr-chart`}>
            {laneScore} ({capacityIndicator})
          </Text>
        </div>
      </div>
    </>
  );
};

const SonarCoverageGuideCard = ({
  response,
}: {
  response: SonarCoverageGuideLane;
}) => {
  const {
    origin_market: originMarket,
    destination_market: destinationMarket,
    current_capacity_condition: currentCondition,
    directional_capacity_change: directionalChange,
    coverage_difficulty_score: difficultyScore,
    coverage_guidance: coverageGuidance,
    outbound_tender_rejection_percent: tenderRejectionPercent,
  } = response;

  return (
    <>
      <Text type="body-sm" isHeavy>
        Coverage Guide
      </Text>
      <div>
        {(originMarket || destinationMarket) && (
          <div className={el`market-condition`}>
            <Text type="body-xs" className={el`location-name`}>
              Market:
            </Text>
            <Text type="body-xs" className={el`sonar-ltr-chart`}>
              {originMarket} → {destinationMarket}
            </Text>
          </div>
        )}
        {difficultyScore != null && (
          <div className={el`market-condition`}>
            <Text type="body-xs" className={el`location-name`}>
              Coverage difficulty:
            </Text>
            <Text type="body-xs" className={el`sonar-ltr-chart`}>
              {difficultyScore}/9 ({currentCondition})
            </Text>
          </div>
        )}
        {directionalChange && (
          <div className={el`market-condition`}>
            <Text type="body-xs" className={el`location-name`}>
              Direction:
            </Text>
            <Text type="body-xs" className={el`sonar-ltr-chart`}>
              {directionalChange}
            </Text>
          </div>
        )}
        {tenderRejectionPercent != null && (
          <div className={el`market-condition`}>
            <Text type="body-xs" className={el`location-name`}>
              Outbound rejection:
            </Text>
            <Text type="body-xs" className={el`sonar-ltr-chart`}>
              {tenderRejectionPercent}%
            </Text>
          </div>
        )}
        {coverageGuidance && (
          <Text type="body-xs" className="text-grey-600 mt-1 italic">
            {coverageGuidance}
          </Text>
        )}
      </div>
    </>
  );
};

export const SonarMarketCondition = (p: SonarMarketConditionProps) => {
  if (
    p.marketCondition.errorMessage &&
    p.marketCondition.integrationType === IntegrationType.SONAR
  )
    return (
      <RateError
        errorMessage={p.marketCondition.errorMessage}
        integrationName="Sonar"
        fetchedEntity="market conditions"
      />
    );

  if (!p.marketCondition.marketConditions.length) return null;

  return (
    <>
      {p.marketCondition.marketConditions.map((mc, idx) => {
        const response = mc?.apiResponse;
        if (!response) return null;

        switch (mc.requestType) {
          case CachedRequestType.MARKET_CONDITIONS_SCORE:
            return (
              <SonarLaneScoreCard
                key={idx}
                response={response as SonarLaneScore}
              />
            );
          case CachedRequestType.SONAR_COVERAGE_GUIDE:
            return (
              <SonarCoverageGuideCard
                key={idx}
                response={response as SonarCoverageGuideLane}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

interface MarketConditionProps {
  marketCondition: MarketConditionsPayload;
}

export const MarketCondition = (p: MarketConditionProps) => {
  return (
    <div>
      {p.marketCondition.integrationType === IntegrationType.GREENSCREENS && (
        <TriumphMarketCondition marketCondition={p.marketCondition} />
      )}
      {p.marketCondition.integrationType === IntegrationType.DAT && (
        <DATMarketCondition marketCondition={p.marketCondition} />
      )}
      {p.marketCondition.integrationType === IntegrationType.SONAR && (
        <SonarMarketCondition marketCondition={p.marketCondition} />
      )}
    </div>
  );
};

interface MarketConditionsProps {
  marketConditions: MarketConditionsPayload[];
  handleRefresh: () => void;
}

export const MarketConditions = (p: MarketConditionsProps) => {
  return (
    <div className={ROOT}>
      <div className={el`header`}>
        <Text type="body-xs">Market conditions</Text>
        <Button
          size="xs"
          variant="light"
          icon={<PiArrowClockwiseFill className="text-gray-700" />}
          onPress={p.handleRefresh}
          tooltip="Refresh market conditions"
        />
      </div>
      <div className={el`market-conditions`}>
        {p.marketConditions.map((mc, index) => (
          <MarketCondition
            key={`${mc.integrationType}-market-condition-${index}`}
            marketCondition={mc}
          />
        ))}
      </div>
    </div>
  );
};
